import { FileRejection } from "@mantine/dropzone";

import { getFileExtension } from "shared";

enum ErrorCodes {
  FileInvalidType = "file-invalid-type",
  FileTooLarge = "file-too-large",
  FileTooSmall = "file-too-small",
  TooManyFiles = "too-many-files",
}

const errorCodesMessage: { [key in ErrorCodes]: string } = {
  "file-too-large": "Le fichier est trop volumineux.",
  "file-invalid-type": "Le type de fichier n'est pas valide.",
  "file-too-small": "Le fichier est trop petit.",
  "too-many-files": "Trop de fichiers ajoutés",
};

type ErrorCode = ErrorCodes | undefined;

type FileError = {
  message: string;
  code: ErrorCodes | string;
};

/**
 * This function validates the file type and extension depending on current Drag & Drop Event
 * When dragging over a dropzone, the file is a DataTransferItem with 'type' key but without extension.
 * So, in this case we need to validate the file type with the mime type.
 * Otherwise, the file is a File, with a 'name' key on which we validate the file extension.
 * This is necessary to ensure that the file is valid before uploading it with all edge cases.
 *
 * @param file File to validate
 * @param validExtensions
 * @param validMimeTypes
 * @returns Object with error message and code if file is invalid, null otherwise
 */
export const fileTypeValidator = (
  file: File | DataTransferItem,
  validExtensions: string[],
  validMimeTypes?: string[],
): FileError | FileError[] | null => {
  if (file instanceof DataTransferItem) {
    if (!validMimeTypes || validMimeTypes.length === 0) {
      return null;
    }
    const AMBIGUOUS_MIME_TYPES = ["text/plain", "application/octet-stream"];
    if (
      file.type === "" ||
      AMBIGUOUS_MIME_TYPES.includes(file.type) ||
      validMimeTypes.includes(file.type)
    ) {
      return null;
    }
  } else {
    if (validExtensions.length === 0) {
      return null;
    }
    if (validExtensions.includes(getFileExtension(file.name))) {
      return null;
    }
  }

  return getFileErrorResponse(ErrorCodes.FileInvalidType);
};

const getFileErrorResponse = (
  errorCode: ErrorCode,
  defaultErrorMessage = "Une erreur est survenue.",
): FileError => {
  let code: ErrorCodes | string = "";

  if (errorCode !== undefined) {
    code = errorCode;
  }

  return {
    code,
    message: getErrorMessage(errorCode) || defaultErrorMessage,
  };
};

export const getFileValidationError = (
  rejectedFiles: FileRejection[],
  defaultErrorMessage?: string,
) => {
  let errorCode: ErrorCode;

  if (rejectedFiles.length > 0) {
    errorCode = rejectedFiles[0]?.errors[0]?.code as ErrorCode;
  }

  return getFileErrorResponse(errorCode, defaultErrorMessage);
};

const getErrorMessage = (code: ErrorCode) => {
  if (!code) {
    return null;
  }

  return errorCodesMessage[code];
};
